import { type TimelineEvent, yearToPixel } from './timeline-utils';
import { EventMarker } from './EventMarker';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { useIsMobile } from '@/lib/hooks/use-mobile';

interface SwimLaneProps {
    label: string;
    icon: string;
    color: string;
    eventRows: TimelineEvent[][];
    minYear: number;
    maxYear: number;
    timelineWidth: number;
    onEventClick: (event: TimelineEvent) => void;
    isSubcategory?: boolean;
    parentLabel?: string;
    isVisible?: boolean;
    onToggleVisibility?: () => void;
    visibleStartYear?: number;
    visibleEndYear?: number;
}

/**
 * Individual swim lane showing events for a category
 */
export function SwimLane({
    label,
    icon,
    color,
    eventRows,
    minYear,
    maxYear,
    timelineWidth,
    onEventClick,
    isSubcategory = false,
    parentLabel,
    isVisible = true,
    onToggleVisibility,
    visibleStartYear,
    visibleEndYear
}: SwimLaneProps) {
    const isMobile = useIsMobile();
    const hasEvents = eventRows.length > 0 && eventRows.some(row => row.length > 0);

    // Calculate total event count once (avoid multiple flat() calls)
    const totalEventCount = hasEvents ? eventRows.reduce((sum, row) => sum + row.length, 0) : 0;

    // Calculate lane height based on number of rows (for stacked events)
    // Reduce height on mobile to use space more efficiently
    const rowCount = eventRows.length;
    const baseHeight = isMobile ? 32 : 40;
    const rowHeight = isMobile ? 24 : 32;
    const minLaneHeight = isMobile ? 48 : 60;

    const calculatedHeight = baseHeight + rowCount * rowHeight;
    const laneHeight = Math.max(minLaneHeight, calculatedHeight); // Min height grows with content

    return (
        <div
            className="flex items-stretch min-h-0 relative border-b border-[#c9b896]/40 flex-1"
            style={{ minHeight: `${laneHeight}px` }}
        >
            {/* Lane header (fixed left) with simplified flat background */}
            <div
                className="sticky left-0 top-0 bottom-0 w-[60px] sm:w-[232px] flex items-center gap-2 px-2 sm:px-3 bg-background/95 backdrop-blur-sm border-r border-border z-30 cursor-pointer hover:bg-accent/50 transition group self-stretch"
                style={{
                    opacity: isVisible ? 1 : 0.5
                }}
                onClick={onToggleVisibility}
            >
                {/* Colored indicator bar */}
                <div
                    className="absolute left-0 top-0 bottom-0 w-1"
                    style={{ backgroundColor: color }}
                />

                {/* Checkbox - Visible on all screens now */}
                {onToggleVisibility && (
                    <input
                        type="checkbox"
                        checked={isVisible}
                        onChange={onToggleVisibility}
                        onClick={(e) => e.stopPropagation()}
                        className="block w-4 h-4 rounded border-2 border-muted-foreground/30 bg-background checked:bg-primary cursor-pointer flex-shrink-0"
                        style={{
                            accentColor: color
                        }}
                    />
                )}

                {/* Icon with Popover - Only icon triggers popover */}
                <Popover>
                    <PopoverTrigger asChild>
                        <span
                            className="text-xl sm:text-xl flex-shrink-0 text-center w-auto cursor-help"
                            onClick={(e) => {
                                e.stopPropagation();
                            }}
                            role="button"
                        >
                            {icon}
                        </span>
                    </PopoverTrigger>
                    <PopoverContent side="right" className="max-w-[200px] p-3" onClick={(e) => e.stopPropagation()}>
                        {isSubcategory && parentLabel && (
                            <div className="text-[10px] text-muted-foreground mb-1">{parentLabel}</div>
                        )}
                        <div className="font-semibold">{label}</div>
                        <div className="text-xs text-muted-foreground mt-2">
                            {hasEvents ? `${totalEventCount} event${totalEventCount !== 1 ? 's' : ''}` : 'No events'}
                        </div>
                    </PopoverContent>
                </Popover>

                <div className="hidden sm:block flex-1 min-w-0">
                    {/* Parent category label for subcategories */}
                    {isSubcategory && parentLabel && (
                        <div className="text-[10px] font-medium text-muted-foreground/70 truncate">
                            {parentLabel}
                        </div>
                    )}
                    <div className={`font-semibold truncate text-foreground/80 ${isSubcategory ? 'text-xs' : 'text-sm'}`}>
                        {label}
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                        {hasEvents ? `${totalEventCount} event${totalEventCount !== 1 ? 's' : ''}` : 'No events'}
                    </div>
                </div>
            </div>

            {/* Lane content area (scrollable) */}
            <div className="absolute left-[60px] sm:left-[232px] right-0 top-0 bottom-0 overflow-visible" style={{ transition: 'none' }}>
                <div className="relative h-full" style={{ width: `${timelineWidth}px`, transition: 'none' }}>
                    {/* Lane background with subtle color */}
                    <div
                        className="absolute inset-0 opacity-[0.08]"
                        style={{ backgroundColor: color, transition: 'none' }}
                    />

                    {/* Event markers */}
                    {hasEvents ? (
                        eventRows.map((row, rowIndex) =>
                            row.map((event, eventIndex) => {
                                // Check if event is visible in viewport (if visibility props are provided)
                                if (visibleStartYear !== undefined && visibleEndYear !== undefined) {
                                    // Add generous padding to avoid clipping long labels or partial events
                                    // Include events that start before visible range but end after it
                                    const isVisible =
                                        (event.sortableYearStart <= visibleEndYear && event.sortableYearEnd >= visibleStartYear) ||
                                        // Or just barely outside but might have long label/icon
                                        (event.sortableYearStart > visibleEndYear && event.sortableYearStart < visibleEndYear + 20) ||
                                        (event.sortableYearEnd < visibleStartYear && event.sortableYearEnd > visibleStartYear - 20);

                                    if (!isVisible) return null;
                                }

                                const startPosition = yearToPixel(
                                    event.sortableYearStart,
                                    minYear,
                                    maxYear,
                                    timelineWidth
                                );
                                const endPosition = yearToPixel(
                                    event.sortableYearEnd,
                                    minYear,
                                    maxYear,
                                    timelineWidth
                                );
                                return (
                                    <EventMarker
                                        key={`${event.title}-${eventIndex}`}
                                        event={event}
                                        startPosition={startPosition}
                                        endPosition={endPosition}
                                        rowIndex={rowIndex}
                                        onClick={onEventClick}
                                    />
                                );
                            })
                        )
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground/50">
                            No events in this period
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

